<template>
    <div>
        <!-- 파일 업로드 섹션 -->
        <div class="upload-section">
            <div class="upload-header">
                <h6 class="section-title">부하목록 업로드</h6>
            </div>
            <div class="upload-content">
                <div class="upload-area">
                    <div class="upload-description">
                        부하 목록이 저장된 엑셀 파일을 업로드하여 선택된 계측기에 부하 정보를 간편하게 구성할 수
                        있습니다.<br />
                        부하 목록을 업로드 하면 없는 부하는 새로 생성하고 이미 존재하는 경우 업로드한 내용으로 부하
                        정보를 갱신합니다.<br />
                        부하 파일을 업로드 하기 전에 반드시 업로드할 파일의 내용이 올바르게 작성되어 있는지 확인 후
                        진행하기를 바랍니다.
                    </div>
                    <div class="file-input-group">
                        <div class="file-display">
                            <i class="fa fas fa-file-excel file-icon"></i>
                            <span class="file-name">{{ fileName || "파일을 선택해주세요" }}</span>
                        </div>
                        <div class="button-group">
                            <button class="btn btn-outline-primary btn-sm" @click="selectFile">
                                <i class="fa fa-folder-open"></i> 파일 선택
                            </button>
                        </div>

                        <input type="file" ref="fileInput" class="d-none" @change="handleFileUpload" />
                    </div>
                </div>
                <div class="button-group-load-list">
                    <button class="btn btn-outline-secondary btn-sm" @click="downloadLoadList">
                        <i class="fa fa-download"></i> 부하 목록 다운로드
                    </button>
                    <button
                        class="btn btn-outline-secondary btn-sm"
                        @click="toggleMappingInfo"
                        :disabled="!hasExistingMapping"
                    >
                        <i class="fa fa-link"></i> 필드 맵핑 설정
                    </button>
                    <button class="btn btn-primary btn-sm" @click="saveData" :disabled="!fileName">
                        <i class="fa fa-upload"></i> 맵핑 설정 및 업로드 실행
                    </button>
                </div>
            </div>
        </div>

        <!-- 헤더 매핑 섹션 -->
        <div
            v-if="headerMappings.length > 0 && isMappingVisible && isEmpty(excelUploadErrorList)"
            class="mapping-section-wrapper"
        >
            <div class="option-group">
                <div class="option-header">
                    <h6 class="option-title">
                        <i class="fa fa-link"></i>
                        필드 맵핑 설정
                    </h6>
                    <!-- <button class="btn btn-success btn-sm" @click="saveMappingInfo">
                        <i class="fa fa-save"></i> 저장
                    </button> -->
                </div>
                <div class="mapping-list">
                    <div class="mapping-grid">
                        <div
                            v-for="(mapping, index) in headerMappings"
                            :key="index"
                            class="mapping-item"
                            :class="{
                                filled: mapping.convertHeader && mapping.convertHeader !== null,
                            }"
                        >
                            <div class="mapping-source">
                                <div class="mapping-value">{{ mapping.excelHeader }}</div>
                                <div class="mapping-dropdown-container" v-if="showSubDesc(mapping.convertHeader)">
                                    <i
                                        class="fa fa-ellipsis-h mapping-ellipsis"
                                        @mouseenter="showDropdown(index)"
                                        @mouseleave="hideDropdown()"
                                    />
                                    <div
                                        v-show="activeDropdownIndex === index"
                                        class="mapping-dropdown"
                                        @mouseenter="showDropdown(index)"
                                        @mouseleave="hideDropdown()"
                                    >
                                        <div
                                            v-for="(item, itemIndex) in getSubDescListItems(mapping.convertHeader)"
                                            :key="itemIndex"
                                            class="dropdown-item"
                                        >
                                            {{ item.text }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="mapping-target">
                                <b-form-select
                                    v-model="mapping.convertHeader"
                                    :options="convertHeaderOptions"
                                    class="mapping-select"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- 오류 목록 섹션 -->
        <div v-if="!isEmpty(excelUploadErrorList)" class="error-section">
            <div class="error-header">
                <h6 class="section-title text-danger">
                    <i class="fa fa-exclamation-triangle"></i>
                    업로드 오류
                </h6>
            </div>
            <div class="error-list">
                <div v-for="(error, index) in excelUploadErrorList" :key="index" class="error-item">
                    <div class="error-row">
                        <span class="error-badge">행 {{ error.row }}</span>
                    </div>
                    <div class="error-message">
                        {{ error.error }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as XLSX from "xlsx";
import backEndApi from "@api/backEndApi.js";
import { mapState } from "vuex";
import * as popupMessages from "@src/consts/popupMessageConsts";
import { extractFilenameFromResponse } from "@src/utils/exportExcelTitle.js";

export default {
    props: {
        newFacility: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            activeDropdownIndex: null,
            showDescList: ["Code", "Zone"],
            // 파일 업로드 관련 데이터
            pointUploadXlsx: null,
            fileName: "",
            headerMappings: [],
            excelUploadErrorList: [],
            hasExistingMapping: {},
            isMappingVisible: false,
            essentialField: ["parentFacilityName", "subFacilityName", "feeder", "propName"],
        };
    },
    mounted() {
        // 기존 매핑 정보가 있으면 초기화
        if (this.newFacility.fieldMapping || this.newFacility.extraFields) {
            this.hasExistingMapping = true;
            this.isMappingVisible = true;
        }
    },
    watch: {
        newFacility: {
            handler(newVal) {
                if (newVal.fieldMapping || newVal.extraFields) {
                    this.hasExistingMapping = true;
                    this.initMapping();
                    this.isMappingVisible = true;
                } else {
                    this.hasExistingMapping = false;
                    this.isMappingVisible = false;
                }
            },
            immediate: true,
            deep: true,
        },
    },
    computed: {
        ...mapState({
            commonCodes: (state) => state.commonCodes,
            zoneInfo: (state) => state.zoneInfo,
        }),
        facilityIdx() {
            return this.newFacility.facilityIdx;
        },
        convertHeaderOptions() {
            // 스토어에서 가져온 옵션들
            const storeOptions =
                this.commonCodes?.headerMapping?.map((v) => ({
                    value: v.value,
                    text: v.text,
                    Desc: v.options?.Desc || v.Desc,
                })) || [];

            // 기본 옵션과 스토어 옵션을 합침
            return [{ value: null, text: "선택" }, ...storeOptions];
        },
    },
    methods: {
        async saveData() {
            if (this.essentialFieldCheck()) {
                try {
                    await this.saveMappingInfo();

                    await this.uploadFile();
                    this.alertNoti(popupMessages.COMMON_SAVE_POPUP_MESSAGE);
                    this.$emit("upload-success");
                } catch (error) {
                    console.error("saveData 오류:", error);
                    // 에러가 발생하면 성공 메시지를 표시하지 않음
                }
            }
        },
        convertHeaderMappingToText(convertHeader) {
            return this.convertHeaderOptions.find((v) => v.value === convertHeader)?.text;
        },
        essentialFieldCheck() {
            let isValid = true;
            this.essentialField.forEach((field) => {
                const found = this.headerMappings.find((mapping) => mapping.convertHeader === field);
                if (!found) {
                    this.alertWarning(`필수 필드(${this.convertHeaderMappingToText(field)})가 선택되지 않았습니다.`);
                    isValid = false;
                }
            });
            return isValid;
        },

        // 마운트 시 기존 매핑 정보가 있으면 매핑 기본값 설정
        initMapping() {
            if (this.newFacility.fieldMapping || this.newFacility.extraFields) {
                this.mappingDefaultAtMount();
            }
        },
        mappingDefaultAtMount() {
            let newHeaderMappings = [];

            if (this.newFacility.fieldMapping) {
                const fieldMappingArr = Object.entries(this.newFacility.fieldMapping).map(
                    ([excelHeader, convertHeader]) => {
                        return {
                            excelHeader,
                            convertHeader,
                        };
                    }
                );
                newHeaderMappings = [...newHeaderMappings, ...fieldMappingArr];
            }

            if (this.newFacility.extraFields) {
                const extraFieldsArr = this.newFacility.extraFields.map((excelHeader) => {
                    return {
                        excelHeader,
                        convertHeader: "archive",
                    };
                });
                newHeaderMappings = [...newHeaderMappings, ...extraFieldsArr];
            }

            // 기존 headerMappings 데이터 보존
            this.headerMappings = [...this.headerMappings, ...newHeaderMappings];
        },
        // 매핑 기본값 설정
        mappingDefaultHeader() {
            // 공통 코드에서 우선적으로 찾고 없을경우 '데이터 보관'으로 설정
            const currentHeaderMappings = [...this.headerMappings] || [];
            currentHeaderMappings.forEach((header) => {
                header.convertHeader = this.findHeaderTextFromCommonCodes(header.excelHeader) || "archive";
            });
            this.headerMappings = currentHeaderMappings;
        },

        // 공통 코드에서 헤더 텍스트 찾기
        findHeaderTextFromCommonCodes(value) {
            const header = this.commonCodes.headerMapping?.find((header) => header.text === value);
            return header ? header.value : null;
        },
        isEmpty(value) {
            return (
                value === null ||
                value === undefined ||
                value === "" ||
                (Array.isArray(value) && value.length === 0) ||
                (typeof value === "string" && value.trim() === "")
            );
        },
        showSubDesc(value) {
            return this.showDescList.includes(this.convertHeaderOptions.find((v) => v.value === value)?.Desc);
        },
        getSubDescList(value, desc) {
            if (desc == "Code") {
                return this.commonCodes[value];
            } else if (desc == "Zone") {
                return this.zoneInfo;
            }
            return [];
        },
        getSubDescListItems(convertHeader) {
            const option = this.convertHeaderOptions.find((v) => v.value === convertHeader);
            if (!option || !option.Desc) return [];

            const desc = option.Desc;
            const list = this.getSubDescList(convertHeader, desc);

            if (Array.isArray(list)) {
                return list;
            } else if (list && list.options) {
                return list.options;
            }
            return [];
        },
        showDropdown(index) {
            this.activeDropdownIndex = index;
        },
        hideDropdown() {
            this.activeDropdownIndex = null;
        },
        async saveMappingInfo() {
            if (!this.headerMappings || this.headerMappings.length === 0) {
                this.alertNoti("저장할 맵핑 정보가 없습니다.");
                return;
            }

            // 매핑 정보만 저장하고 파일은 유지
            await this.updateFacilityMapping();

            // 성공 메시지 표시
            // this.alertNoti("매핑 정보가 저장되었습니다.");
        },

        // 파일 업로드 관련 메서드들
        selectFile() {
            this.$refs.fileInput.click();
        },

        async uploadFile() {
            if (!this.pointUploadXlsx) {
                this.alertNoti("업로드할 파일을 먼저 선택해주세요.");
                throw new Error("업로드할 파일이 없습니다.");
            }

            try {
                const result = await backEndApi.facility.uploadPointInfoByFacility(
                    this.facilityIdx,
                    this.pointUploadXlsx
                );

                if (result.status !== 200) {
                    if (result.data.detail?.errors?.length > 0) {
                        this.excelUploadErrorList = result.data.detail.errors;
                    }
                    throw new Error(result.data.message);
                }
                return;
            } catch (e) {
                this.alertWarning(e.message);
                console.error(e);
                throw e; // 에러를 상위로 전파
            }
        },

        async downloadLoadList() {
            try {
                const response = await backEndApi.facility.downloadPointInfoByFacility(this.facilityIdx);

                if (response.status === 200 && response.data) {
                    const fileName = extractFilenameFromResponse(response, "부하목록.xlsx");

                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);

                    this.alertNoti("부하목록 다운로드가 시작되었습니다.");
                } else {
                    throw new Error(`다운로드 실패: ${response || "알 수 없는 오류"}`);
                }
            } catch (e) {
                this.alertWarning(e.message);
                console.error(e);
                return;
            }
        },

        toggleMappingInfo() {
            this.isMappingVisible = !this.isMappingVisible;
        },
        handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                // 새로운 파일이 업로드되면 오류 리스트와 헤더 매핑만 초기화 (파일은 유지)
                this.excelUploadErrorList = [];
                this.headerMappings = [];
                this.fileName = file.name;
                this.pointUploadXlsx = file;

                const reader = new FileReader();
                reader.onload = (e) => {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });

                    // Set 객체를 사용해서 모든 탭에서 유니크한 헤더들을 수집
                    const uniqueHeaders = new Set();

                    // 모든 시트(탭)를 순회하며 헤더 추출
                    workbook.SheetNames.forEach((sheetName) => {
                        const worksheet = workbook.Sheets[sheetName];
                        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                        if (jsonData.length > 0 && Array.isArray(jsonData[0])) {
                            // 각 시트의 첫 번째 행(헤더)을 Set에 추가
                            jsonData[0].forEach((header) => {
                                if (header && header.toString().trim() !== "") {
                                    uniqueHeaders.add(header.toString().trim());
                                }
                            });
                        }
                    });

                    // Set을 배열로 변환하여 headerMappings 생성
                    if (uniqueHeaders.size > 0) {
                        this.headerMappings = Array.from(uniqueHeaders).map((header) => ({
                            excelHeader: header,
                            convertHeader: null,
                        }));
                        // 매핑 기본값 설정
                        this.mappingDefaultHeader();
                        // 매핑펼침
                        this.isMappingVisible = true;
                        // 헤더매핑 존재 토글
                        this.hasExistingMapping = true;
                    }
                };

                reader.readAsArrayBuffer(file);
            }
        },
        // 계측기 데이터 업데이트
        async updateFacilityData() {
            const { status, data } = await backEndApi.facility.updateFacility(this.newFacility);

            if (status !== 200) {
                throw new Error(data.message);
            }
            return;
        },

        // 매핑 정보 업데이트
        async updateFacilityMapping() {
            if (!this.headerMappings || this.headerMappings.length === 0) return;

            const notSaveList = this.headerMappings.filter((header) => header.convertHeader != "archive");
            const saveList = this.headerMappings.filter((header) => header.convertHeader == "archive");

            // fieldMapping이 null이면 빈 객체로 초기화
            if (!this.newFacility.fieldMapping) {
                this.$set(this.newFacility, "fieldMapping", {});
            }

            this.newFacility.fieldMapping = Object.fromEntries(
                notSaveList.map((header) => [header.excelHeader, header.convertHeader])
            );
            this.newFacility.extraFields = saveList.map((header) => header.excelHeader);

            // 확인 후 업데이트 실행
            try {
                const result = await this.alertQuestion(popupMessages.COMMON_UPDATE_CONFIRM_POPUP_MESSAGE);
                if (!result.value) return;
                await this.updateFacilityData();
            } catch (e) {
                console.error("updateFacilityMapping 오류:", e);
            }
        },
    },
};
</script>

<style scoped>
.upload-description {
    font-size: 12px;
    /* font-weight: 600; */
    color: #6c757d;
    line-height: 1.5;
    margin-bottom: 12px;
}
.button-group-load-list {
    width: 200px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 12px;
}
.upload-content {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
}
/* 옵션 그룹 스타일 */
.option-group {
    margin-bottom: 20px;
    padding: 15px;
    background: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
}

.option-group:last-child {
    margin-bottom: 0;
}

.option-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #e9ecef;
}

.option-title {
    margin: 0;
    color: #495057;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 6px;
}

.option-title i {
    color: #6c757d;
    font-size: 12px;
}

/* 파일 업로드 섹션 스타일 */
.upload-section {
    margin-bottom: 1rem;
}

.upload-header {
    padding: 8px 0;
    margin-bottom: 10px;
}

.upload-area {
    background: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 14px;
    transition: all 0.3s ease;
    flex: 1;
    min-width: 0;
}

.file-input-group {
    display: flex;
    /* flex-direction: column; */
    gap: 1rem;
    width: 100%;
    height: 40%;
}

.file-display {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: white;
    border-radius: 6px;
    border: 1px solid #e9ecef;
    width: 85%;
}

.file-icon {
    font-size: 1.5rem;
    color: #28a745;
}

.file-name {
    font-size: 0.9rem;
    color: #495057;
    font-weight: 500;
}

.button-group {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-start;
}

/* 버튼 그룹 내 버튼 스타일 개선 */
.button-group-load-list .btn {
    width: 100%;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 36px;
    border: 1px solid #dee2e6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 섹션 제목 공통 스타일 */
.section-title {
    font-size: 13px;
    font-weight: 600;
    margin: 0;
    color: #495057;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

/* 매핑 섹션 래퍼 스타일 */
.mapping-section-wrapper {
    padding: 15px;
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 8px;
}

/* 매핑 리스트 스타일 */
.mapping-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    max-height: 350px;
    overflow-y: auto;
}

.mapping-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 0.75rem;
    max-width: 100%;
}

.mapping-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    transition: all 0.3s ease;
    min-height: 80px;
}

.mapping-item:hover {
    border-color: #002060;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.mapping-item.filled {
    border: 1px solid #42a5f5;
    background: #f8faff;
    box-shadow: 0 2px 6px rgba(13, 110, 253, 0.15);
}

.mapping-item.filled:hover {
    border-color: #0b5ed7;
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.25);
}

.mapping-source,
.mapping-target {
    flex: 1;
    display: flex;
    gap: 0.2rem;
    width: 100%;
    justify-content: space-between;
    align-items: center;
}

.mapping-value {
    font-size: 12px;
    font-weight: 700;
    color: #495057;
    padding: 0.3rem;
    border-radius: 4px;
    line-height: 1.3;
    text-align: left;
    max-width: 15em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.mapping-select {
    border: 1px solid #e9ecef;
    border-radius: 4px;
    height: 30px;
    padding: 0.3rem;
    background: #f8f9fa;
    font-size: 0.8rem;
    font-weight: 500;
    color: #495057;
    line-height: 1.3;
    appearance: auto;
    -webkit-appearance: auto;
    -moz-appearance: auto;
}

/* 드롭다운 스타일 */
.mapping-ellipsis {
    font-size: 14px;
    color: #6c757d;
    cursor: pointer;
    margin-right: 0.2rem;
    padding: 0.3rem;
}

.mapping-ellipsis:hover {
    color: #002060;
    background-color: #f1f3f4;
    border-radius: 50%;
    padding: 0.3rem;
    display: flex;
    justify-content: center;
    align-items: center;
}

.mapping-dropdown-container {
    position: relative;
    display: inline-block;
}

.mapping-dropdown {
    position: absolute;
    top: 100%;
    right: 0;
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    min-width: 150px;
    max-height: 90px;
    overflow-y: auto;
    padding: 0.5rem 0;
}

.dropdown-item {
    padding: 0.5rem 1rem;
    font-size: 0.7rem;
    color: #495057;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #f8f9fa;
}

.dropdown-item:last-child {
    border-bottom: none;
}

/* 오류 섹션 스타일 */
.error-section {
    margin-bottom: 2rem;
}

.error-header {
    margin-bottom: 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #e9ecef;
}

.error-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.error-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    background: #fff5f5;
    border: 1px solid #fed7d7;
    border-radius: 8px;
    border-left: 4px solid #e53e3e;
}

.error-row {
    flex-shrink: 0;
}

.error-badge {
    background: #e53e3e;
    color: white;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
}

.error-message {
    flex: 1;
    font-size: 0.9rem;
    color: #c53030;
    line-height: 1.4;
}
</style>
